import { FwVersion, FwVersionInfo } from 'src/dtos/version.dto';
import { BaseStore } from './base.store';
import { RootStore } from './index';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { isValidatedOrInactive } from 'src/utils/versionUtils';
import {  OnBoardingStatus } from 'src/utils/enums';
import fwVersionService from 'src/services/fw-version.service';
import { convertDateRange, DateRange } from '../../utils/datesUtils';

export class DashboardStore extends BaseStore {
    public isLoading: boolean = false;
    public isLoadingAactiveVersion: boolean = false;
    public activeVersionData: FwVersion | undefined = undefined;
    public dateRange: DateRange = 'Last 7 days';

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            isLoading: observable,
            isLoadingAactiveVersion: observable,
            activeVersionData: observable,
            needToBeginOnBoarding: computed,
            isOnboardingInProgress: computed,
            isAfterOnBoarding: computed,
            versionForDashboard: computed,
            currentOnBoardingStep: computed,
            loadVersionData: action,
            setDateRange: action,
        });
    }

    initializeRange() {
        const storedRange = localStorage.getItem(`dashboardDateRange-${this.rootStore.customerStore.serviceId}`);
        if (storedRange) {
            runInAction(() => {
                this.dateRange = convertDateRange(JSON.parse(storedRange));
            });
        } else {
            this.dateRange = 'Last 7 days';
        }
    }

    get needToBeginOnBoarding() {
        const fwVersionStore = this.rootStore.fwVersionStore;
        return fwVersionStore.fwVersions?.length === 1 && fwVersionStore.getActiveVersion;
    }

    get isOnboardingInProgress() {
        const fwVersionStore = this.rootStore.fwVersionStore;
        return fwVersionStore.fwVersions?.length === 2 && fwVersionStore.getActiveVersion && fwVersionStore.pendingVersion;
    }

    get isAfterOnBoarding() {
        const fwVersionStore = this.rootStore.fwVersionStore;
        if (!fwVersionStore.fwVersions || fwVersionStore.fwVersions.length === 1) return false;
        return fwVersionStore.fwVersions.length >= 2 || fwVersionStore.getActiveVersion && fwVersionStore.fwVersions.find(isValidatedOrInactive);
    }

    get versionForDashboard(): FwVersionInfo | undefined {
        const { Start, InProgress, Finish } = OnBoardingStatus;
        switch (this.currentOnBoardingStep) {
            case Start: return undefined;
            case InProgress: return this.rootStore.fwVersionStore.pendingVersion;
            case Finish: return this.rootStore.fwVersionStore.getActiveVersion;
            default: return undefined;
        }
    }

    get currentOnBoardingStep() {
        const { Start, InProgress, Finish } = OnBoardingStatus;
        if (this.needToBeginOnBoarding) return Start;
        if (this.isOnboardingInProgress) return InProgress;
        if (this.isAfterOnBoarding) return Finish;
        return undefined;
    }


    public async loadVersionData() {
        try {
            if (!this.versionForDashboard) return;
            runInAction(() =>
                this.isLoadingAactiveVersion = true
            );

            const { externalId } = this.versionForDashboard;
            const res = await fwVersionService.getVersionData(this.rootStore.customerStore.customerId, this.rootStore.customerStore.serviceId, externalId);
            runInAction(() =>
                this.activeVersionData = res
            );
        } catch (e) {
            console.error(e);
        } finally {
            runInAction(() =>
                this.isLoadingAactiveVersion = false
            );
        }
    }

    public setDateRange(dateRange: string | DateRange) {
        this.dateRange = dateRange;
        localStorage.setItem(`dashboardDateRange-${this.rootStore.customerStore.serviceId}`, JSON.stringify(dateRange));
    }
}
